import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { readCaseState } from 'common/External/state';

export const MAX_WORKSTATION_ID_LENGTH = 255;

export type SaveWorkstationIDResult =
  | { ok: true; workstationID: string }
  | { ok: false; error: string };

export const useWorkstationID = () => {
  const { workstationID } = useRecoilValue(readCaseState);
  const setReadCaseState = useSetRecoilState(readCaseState);

  const clearWorkstationID = useCallback(() => {
    setReadCaseState((current) => ({ ...current, workstationID: null }));
  }, [setReadCaseState]);

  const saveWorkstationID = useCallback(
    (value: string): SaveWorkstationIDResult => {
      const trimmedWorkstationID = value.trim();

      if (trimmedWorkstationID === '') {
        return {
          ok: false,
          error: 'Please enter a non-empty workstation ID.',
        };
      }

      if (trimmedWorkstationID.length > MAX_WORKSTATION_ID_LENGTH) {
        return {
          ok: false,
          error: `Workstation ID must be ${MAX_WORKSTATION_ID_LENGTH} characters or fewer.`,
        };
      }

      setReadCaseState((current) => ({ ...current, workstationID: trimmedWorkstationID }));

      return {
        ok: true,
        workstationID: trimmedWorkstationID,
      };
    },
    [setReadCaseState]
  );

  return { workstationID, saveWorkstationID, clearWorkstationID };
};
